import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { GriddDocument } from "@/types/document";

// Per-embed grid bodies, keyed by griddDocId. Kept out of documentSlice because
// embeds emit a full document every ~400ms while typing; folding bodies into the
// deck record would rewrite the whole deck on every keystroke burst (TODO §7.2).
export type GriddState = Record<string, GriddDocument>;

const initialState: GriddState = {};

const griddSlice = createSlice({
  name: "gridd",
  initialState,
  reducers: {
    // §17.4.5 MERGES a map in, rather than replacing it — the nugget path's one new
    // reducer. Inserting a nugget brings its grid bodies into a session that is already
    // holding this deck's, so `hydrated` (which replaces) would wipe the deck.
    //
    // It is deliberately NOT in the persistence listener's matcher: these bodies were just
    // READ out of IndexedDB under the nugget's owner key, and writing them straight back —
    // under this deck's owner, which is what a naive listener would do — is the exact fork
    // §17.1.5 exists to prevent.
    griddDocsMerged(state, action: PayloadAction<GriddState>) {
      Object.assign(state, action.payload);
    },
    // Replaces the whole map on IndexedDB hydration (TODO §4.4).
    hydrated(_state, action: PayloadAction<GriddState>) {
      return action.payload;
    },
    griddDocCreated(
      state,
      action: PayloadAction<{ docId: string; doc: GriddDocument }>,
    ) {
      state[action.payload.docId] = action.payload.doc;
    },
    griddDocUpdated(
      state,
      action: PayloadAction<{ docId: string; doc: GriddDocument }>,
    ) {
      state[action.payload.docId] = action.payload.doc;
    },
    griddDocRemoved(state, action: PayloadAction<{ docId: string }>) {
      delete state[action.payload.docId];
    },
  },
});

export const {
  hydrated,
  griddDocsMerged,
  griddDocCreated,
  griddDocUpdated,
  griddDocRemoved,
} = griddSlice.actions;

export default griddSlice.reducer;
